import React, { useState, useEffect } from 'react';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useRoles } from '../../hooks/useRoles';
import { useDepartments } from '../../hooks/useDepartments';
import { useOrgUnitLevels } from '../../hooks/useOrgUnitLevels';
import { useAuth } from '../../contexts/AuthContext';
import { StyledEngineProvider } from '@mui/material';

export default function RoleModal({ isOpen, onClose, onSave, editData, title, isLoading = false, isViewMode = false }) {
    const { user } = useAuth();

    // --- State Management ---
    const [formData, setFormData] = useState({
        name: '',
        org: '',
        department: '', // Added department field
        is_system: true,
        org_unit_level_id: ''
    });

    // --- Data Fetching ---
    const { data: orgsData, isLoading: orgsLoading } = useOrganizations();
    const organizations = orgsData?.results || orgsData || [];

    // Fetch roles based on selected organization
    const { data: rolesData, isLoading: rolesLoading } = useRoles(formData.org);
    const rolesList = rolesData?.results || rolesData || [];

    // Fetch departments based on selected organization
    const { data: deptData, isLoading: deptLoading } = useDepartments(formData.org);
    const departments = deptData?.results || deptData || [];

    // Fetch units based on selected department (REMOVED as we are now using Org Unit Levels)
    // const { data: unitsData, isLoading: unitsLoading } = useUnits(formData.department, formData.org);
    // const unitsList = unitsData?.results || unitsData || [];

    // Fetch Org Unit Levels based on selected organization
    const { data: levelsData, isLoading: levelsLoading } = useOrgUnitLevels({ orgId: formData.org }, { enabled: !!formData.org });
    const levelsList = levelsData?.results || levelsData || [];

    const isSuperAdmin = user?.is_superuser || user?.role === 'super_admin';

    // --- Lifecycle: Sync Data ---
    useEffect(() => {
        if (isOpen) {
            if (editData) {
                setFormData({
                    name: editData.name || '',
                    org: editData.org_id || editData.org?.id || editData.org || '',
                    department: editData.department_id || editData.department?.id || editData.department || '',
                    is_system: editData.is_system ?? true,
                    org_unit_level_id: editData.org_unit_level_id || editData.org_unit_level?.id || editData.org_unit_level || ''
                });
            } else {
                setFormData({
                    name: '',
                    org: isSuperAdmin ? '' : (user?.orgId || ''),
                    is_system: true,
                    org_unit_level_id: ''
                });
            }
        }
    }, [editData, isOpen, isSuperAdmin, user?.orgId]);

    // --- Logic Helpers ---
    const generateCode = (name) => {
        return (name || '').toUpperCase().trim().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            org: formData.org,
            code: generateCode(formData.name),
            name: formData.name,
            is_system: formData.is_system,
            name: formData.name,
            is_system: formData.is_system,
            org_unit_level_id: formData.org_unit_level_id,
            org_unit_level: formData.org_unit_level_id, // Confirmed from backend: this is the correct Serializer key
            department: formData.department
        };
        const headerOrgId = isSuperAdmin ? null : formData.org;
        onSave(payload, headerOrgId);
    };

    if (!isOpen) return null;

    return (
        <StyledEngineProvider injectFirst>
            <div className="fixed p-15 w-full max-w-[760px] left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[1001]">
                <div className='py-15 md:px-25 px-15 bg-bg-dark rounded-lg shadow-xl border border-border'>
                    <h5 className="text-[16px] font-medium py-10 mb-15 text-center border-b border-border">{title}</h5>

                    <form onSubmit={handleSubmit} className="space-y-10 px-5">
                        <div className="flex flex-col gap-15">
                            {/* Organization Select */}
                            <div className='flex flex-col'>
                                <label className='mb-1 text-xs font-medium text-text-main'>Organization *</label>
                                {isViewMode ? (
                                    <div className='bg-bg-light border border-border py-2 px-3 rounded-[4px] text-sm text-text-main'>
                                        {organizations.find(o => String(o.id) === String(formData.org))?.name || '-'}
                                    </div>
                                ) : (
                                    <select
                                        value={formData.org}
                                        onChange={(e) => setFormData({ ...formData, org: e.target.value, department: '', org_unit_level_id: '' })}
                                        required
                                        disabled={!isSuperAdmin || isLoading || orgsLoading}
                                        className='bg-bg-light border border-border py-2 px-3 rounded-[4px] focus:outline-none text-sm text-text-main'
                                    >
                                        <option value="">{orgsLoading ? 'Loading...' : 'Select Organization'}</option>
                                        {organizations.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
                                    </select>
                                )}
                            </div>

                            {/* Department Select */}
                            <div className='flex flex-col'>
                                <label className='mb-1 text-xs font-medium text-text-main'>Department *</label>
                                {isViewMode ? (
                                    <div className='bg-bg-light border border-border py-2 px-3 rounded-[4px] text-sm text-text-main'>
                                        {departments.find(d => String(d.id) === String(formData.department))?.name || '-'}
                                    </div>
                                ) : (
                                    <select
                                        value={formData.department}
                                        onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                                        required
                                        disabled={!formData.org || deptLoading}
                                        className='bg-bg-light border border-border py-2 px-3 rounded-[4px] focus:outline-none text-sm text-text-main'
                                    >
                                        <option value="">{deptLoading ? 'Loading...' : 'Select Department'}</option>
                                        {departments.map(dept => <option key={dept.id} value={dept.id}>{dept.name}</option>)}
                                    </select>
                                )}
                            </div>

                            <div className='flex justify-between items-center gap-4'>
                                {/* Role Name */}
                                <div className='flex flex-col w-full'>
                                    <label className='mb-1 text-xs font-medium text-text-main'>Role Name *</label>
                                    {isViewMode ? (
                                        <div className='bg-bg-light border border-border py-2 px-3 rounded-[4px] text-sm text-text-main'>
                                            {formData.name || '-'}
                                        </div>
                                    ) : (
                                        <input
                                            type='text'
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            placeholder='e.g. Content Manager'
                                            className='bg-bg-light border border-border py-2 px-3 rounded-[4px] focus:outline-none text-sm text-text-main'
                                            required
                                        />
                                    )}
                                    {!isViewMode && formData.name && (
                                        <span className='text-[10px] text-text-secondary mt-1'>
                                            System Code: {generateCode(formData.name)}
                                        </span>
                                    )}
                                </div>
                                {/* Role unit (Dropdown) */}
                                <div className='flex flex-col w-full'>
                                    <label className='mb-1 text-xs font-medium text-text-main'>Organisation Unit Level *</label>
                                    {isViewMode ? (
                                        <div className='bg-bg-light border border-border py-2 px-3 rounded-[4px] text-sm text-text-main'>
                                            {levelsList.find(l => String(l.id) === String(formData.org_unit_level_id))?.name || '-'}
                                        </div>
                                    ) : (
                                        <select
                                            value={formData.org_unit_level_id}
                                            onChange={(e) => setFormData({ ...formData, org_unit_level_id: e.target.value })}
                                            className='bg-bg-light border border-border py-2 px-3 rounded-[4px] focus:outline-none text-sm text-text-main'
                                            required
                                            disabled={isLoading || levelsLoading}
                                        >
                                            <option value="">{levelsLoading ? 'Loading...' : '-- Select Level --'}</option>
                                            {levelsList.map(level => (
                                                <option key={level.id} value={level.id}>
                                                    {level.name} (Rank: {level.rank})
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className='flex gap-10 justify-end mt-15 pt-10 border-t border-border'>
                            <button
                                type='button'
                                onClick={onClose}
                                className='btn btn-outline-secondary btn-sm'
                            >
                                {isViewMode ? 'Close' : 'Cancel'}
                            </button>
                            {!isViewMode && (
                                <button
                                    type='submit'
                                    className='btn btn-primary btn-sm'
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Saving...' : (editData ? 'Update Role' : 'Submit')}
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>
            {/* Overlay */}
            <div onClick={onClose} className='fixed z-[1000] w-full h-full left-0 top-0 bg-black/50 backdrop-blur-[2px]'></div>
        </StyledEngineProvider>
    );
}